'use client'

import React, { FormEvent, useEffect, useId, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { CRITERIOS_AVANZADA_KEY, MENSAJE_E2, TEXTO_BUSQUEDA_KEY } from '@/lib/resultados'

type RespuestaErrorBusqueda = {
  codigo?: string
  mensaje?: string
  error?: string
  causa?: string
}

const AYUDA_LONGITUD_ID = 'busqueda-basica-ayuda'
const ERROR_ID = 'busqueda-basica-error'

export default function BusquedaBasicaHeader() {
  const router = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const tituloId = useId()
  const [texto, setTexto] = useState('')
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [abierto, setAbierto] = useState(false)
  const [anclaje, setAnclaje] = useState({ top: 0, right: 16 })

  useEffect(() => {
    const guardado = sessionStorage.getItem(TEXTO_BUSQUEDA_KEY)
    if (guardado !== null) {
      setTexto(guardado)
    }
  }, [])

  useEffect(() => {
    if (!abierto) {
      return
    }
    const rect = panelRef.current?.getBoundingClientRect()
    if (rect) {
      setAnclaje({ top: rect.bottom + 8, right: Math.max(16, window.innerWidth - rect.right) })
    }
    inputRef.current?.focus()
    function alClic(evento: MouseEvent) {
      if (!panelRef.current?.contains(evento.target as Node)) {
        setAbierto(false)
      }
    }
    function alTecla(evento: KeyboardEvent) {
      if (evento.key === 'Escape') {
        setAbierto(false)
      }
    }
    document.addEventListener('mousedown', alClic)
    document.addEventListener('keydown', alTecla)
    return () => {
      document.removeEventListener('mousedown', alClic)
      document.removeEventListener('keydown', alTecla)
    }
  }, [abierto])

  async function buscar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError(null)
    setCargando(true)
    sessionStorage.setItem(TEXTO_BUSQUEDA_KEY, texto)

    try {
      const respuesta = await fetch(
        `/api/backend/v1/busqueda/basica?q=${encodeURIComponent(texto)}&pagina=1&tamanio=20`,
        {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        },
      )
      const cuerpo = await respuesta.json().catch(() => null)

      if (respuesta.status === 401) {
        const detalle = cuerpo as RespuestaErrorBusqueda | null
        setError(detalle?.error ?? 'Sesión expirada. Por favor inicia sesión nuevamente.')
        window.setTimeout(() => {
          window.location.href = '/api/auth/logout'
        }, 2500)
        return
      }

      if (respuesta.status === 400) {
        const detalle = cuerpo as RespuestaErrorBusqueda | null
        setError(
          detalle?.mensaje ??
            'No fue posible validar la búsqueda. Verifique el criterio e intente de nuevo.',
        )
        return
      }

      if (!respuesta.ok) {
        setError(
          typeof cuerpo?.mensaje === 'string'
            ? cuerpo.mensaje
            : typeof cuerpo?.error === 'string'
              ? cuerpo.error
              : MENSAJE_E2,
        )
        return
      }

      sessionStorage.removeItem(CRITERIOS_AVANZADA_KEY)
      setAbierto(false)
      router.push(
        `/gestion/busqueda/resultados?modo=basica&q=${encodeURIComponent(texto)}&pagina=1`,
      )
    } catch {
      setError(MENSAJE_E2)
    } finally {
      setCargando(false)
    }
  }

  return (
    <div className="bh-busqueda-ancla search-tooltip-trigger" ref={panelRef}>
      <button
        id="busqueda-simple-abrir"
        type="button"
        className="bh-lupa"
        aria-label="buscar"
        aria-describedby="busqueda-basica-tooltip"
        aria-expanded={abierto}
        aria-controls="busqueda-simple-panel"
        onClick={() => {
          setError(null)
          setAbierto((valor) => !valor)
        }}
      >
        ⌕
      </button>
      <span id="busqueda-basica-tooltip" role="tooltip" className="search-tooltip">
        Buscar
      </span>

      {abierto && (
        <div
          id="busqueda-simple-panel"
          className="bh-busqueda-panel"
          style={{ top: anclaje.top, right: anclaje.right }}
          role="dialog"
          aria-labelledby={tituloId}
        >
          <div className="bh-busqueda-cabecera">
            <h2 id={tituloId} className="bh-tarjeta-titulo mb-0">
              <span id="busqueda-simple-titulo">Búsqueda básica</span>
            </h2>
            <button
              id="busqueda-simple-cerrar"
              type="button"
              className="bh-cerrar"
              aria-label="Cerrar búsqueda"
              onClick={() => setAbierto(false)}
            >
              ×
            </button>
          </div>
          <form onSubmit={buscar} aria-busy={cargando || undefined}>
            <div className="bh-barra-fila">
              <input
                ref={inputRef}
                id="busqueda-basica-q"
                className="bh-barra"
                name="q"
                type="text"
                value={texto}
                placeholder="Buscar…"
                aria-label="Búsqueda básica"
                aria-describedby={`${AYUDA_LONGITUD_ID}${error ? ` ${ERROR_ID}` : ''}`}
                aria-invalid={Boolean(error) || undefined}
                disabled={cargando}
                maxLength={240}
                onChange={(evento) => setTexto(evento.target.value)}
              />
              <button type="submit" className="bh-buscar-naranja" disabled={cargando} aria-label="Buscar">
                {cargando ? '…' : '⌕'}
              </button>
            </div>
            <span id={AYUDA_LONGITUD_ID} className="visually-hidden">
              Entre 2 y 240 caracteres.
            </span>
          </form>
          <Link
            id="busqueda-simple-avanzada"
            className="bh-enlace-avanzada"
            href="/gestion/busqueda"
            onClick={() => setAbierto(false)}
          >
            Búsqueda avanzada
          </Link>
          {error && (
            <div id={ERROR_ID} className="bh-error" role="alert" aria-live="assertive">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
